import { useSyncExternalStore } from 'react'
import { onEdgeEvent } from '../servers/commandStream'
import type { RestoreJob } from '../transport/api'

/**
 * WHERE EACH RESTORE IS UP TO — the restoring machine announces every step as a
 * `restore_progress` edge event (edge agentRestore.ts). Module-level, like backupJobs, so
 * the form can close and open again and still show the restore that is running.
 * Never backwards: a finished restore is not un-finished by an event overtaken on the way.
 */
const jobs = new Map<string, RestoreJob & { device: string }>()
const listeners = new Set<() => void>()

export const isOver = (j: RestoreJob | null | undefined) => j?.phase === 'done' || j?.phase === 'failed'

export function noteRestoreJob(device: string, job: RestoreJob): void {
  const prev = jobs.get(job.job_id)
  if (prev && isOver(prev) && !isOver(job)) return
  if (prev && JSON.stringify(prev) === JSON.stringify({ ...job, device })) return
  jobs.set(job.job_id, { ...job, device })
  for (const l of [...listeners]) l()
}

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

export function useRestoreJob(jobId: string | null): (RestoreJob & { device: string }) | null {
  return useSyncExternalStore(subscribe, () => (jobId ? (jobs.get(jobId) ?? null) : null))
}

onEdgeEvent((event, data) => {
  if (event !== 'restore_progress') return
  const job = data['job'] as RestoreJob | undefined
  const device = typeof data['device_id'] === 'string' ? data['device_id'] : ''
  if (job && typeof job.job_id === 'string' && device) noteRestoreJob(device, job)
})

/** Tests only. */
export function __resetRestoreJobs(): void {
  jobs.clear()
}
